'use client';

import { c as _c } from "react/compiler-runtime";
import 'keen-slider/keen-slider.min.css';
import type { KeenSliderInstance, KeenSliderOptions } from 'keen-slider/react';
import { useKeenSlider } from 'keen-slider/react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { mouseWheelPlugin } from '@src/utils/slider.utils';
import { cn } from '@src/utils/tailwind';
import { Arrow } from './Arrow';
import type { ArrowAlignment } from './useArrowYOffset';
export const slideClassName = 'keen-slider__slide';
export const positionThresholdRange = 0.01;
export type SliderOptions = KeenSliderOptions;
interface SliderProps {
  children: ReactNode;
  options: SliderOptions;
  className?: string;
  hasArrows?: boolean;
  /** Defaults to 'image'. */
  arrowAlignment?: ArrowAlignment;
  isDarkMode?: boolean;
  navigation?: (fn: KeenSliderInstance | null) => ReactNode;
}
export function SliderV2(props) {
  const $ = _c(37);
  const [slider, setSlider] = useState();
  const [isOnInitialPosition, setIsOnInitialPosition] = useState(!props.options?.loop);
  const [isOnFinalPosition, setIsOnFinalPosition] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = progress => {
      const onInitialPosition = Number.isNaN(progress) || progress <= 0;
      const onFinalPosition = Number.isNaN(progress) || progress + positionThresholdRange >= 1;
      setIsOnInitialPosition(onInitialPosition);
      setIsOnFinalPosition(onFinalPosition);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const calculateRailPosition = t0;
  let t1;
  if ($[1] !== props.options) {
    t1 = e => {
      if (!props.options?.loop) {
        calculateRailPosition(e.track.details?.progress);
      }
      if (props.options?.detailsChanged) {
        props.options?.detailsChanged(e);
      }
    };
    $[1] = props.options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.options || $[4] !== t1) {
    t2 = {
      ...props.options,
      detailsChanged: t1
    };
    $[3] = props.options;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const alteredOptions = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [mouseWheelPlugin];
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [sliderRef, instanceRef] = useKeenSlider(alteredOptions, t3);
  let t4;
  let t5;
  if ($[7] !== instanceRef) {
    t4 = () => {
      if (instanceRef.current) {
        setSlider(instanceRef.current);
      }
    };
    t5 = [instanceRef];
    $[7] = instanceRef;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== instanceRef || $[11] !== props.options?.initial) {
    t6 = () => {
      if (props.options?.initial) {
        setTimeout(() => {
          instanceRef.current?.update(undefined, props.options?.initial);
        });
      }
    };
    $[10] = instanceRef;
    $[11] = props.options?.initial;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== instanceRef || $[14] !== props.options) {
    t7 = [calculateRailPosition, instanceRef, props.options];
    $[13] = instanceRef;
    $[14] = props.options;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  useEffect(t6, t7);
  const t8 = slider ? "visible" : "invisible";
  let t9;
  if ($[16] !== props.className || $[17] !== t8) {
    t9 = cn("keen-slider overflow-visible! lg:overflow-hidden!", t8, props.className);
    $[16] = props.className;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== props.children || $[20] !== sliderRef || $[21] !== t9) {
    t10 = <div ref={sliderRef} className={t9}>{props.children}</div>;
    $[19] = props.children;
    $[20] = sliderRef;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== isOnFinalPosition || $[24] !== isOnInitialPosition || $[25] !== props.arrowAlignment || $[26] !== props.hasArrows || $[27] !== props.isDarkMode || $[28] !== slider) {
    t11 = props.hasArrows && slider && <><Arrow isLeft={true} onClick={slider.prev} isDarkMode={props.isDarkMode} alignTo={props.arrowAlignment} isVisible={!isOnInitialPosition} /><Arrow onClick={slider.next} isDarkMode={props.isDarkMode} alignTo={props.arrowAlignment} isVisible={!isOnFinalPosition} /></>;
    $[23] = isOnFinalPosition;
    $[24] = isOnInitialPosition;
    $[25] = props.arrowAlignment;
    $[26] = props.hasArrows;
    $[27] = props.isDarkMode;
    $[28] = slider;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== props.navigation || $[31] !== slider) {
    t12 = props.navigation && slider && props.navigation(slider);
    $[30] = props.navigation;
    $[31] = slider;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t12) {
    t13 = <div className="relative -mx-4 overflow-hidden px-4 sm:-mx-8 sm:px-8 lg:mx-0 lg:overflow-visible lg:px-0" data-slider-v2-wrapper={true}>{t10}{t11}{t12}</div>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}